import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useUser } from '@descope/react-sdk';
import { getAllCustomersApi } from 'api/adminApi';
import { getCustomerPipelineApi } from 'api/customerCockpitProfileApi';
import {
  createAccountingInvoiceApi,
  getCustomerBillingDetailsApi,
  listCustomerInvoiceNumbersApi,
  upsertCustomerBillingDetailsApi,
} from 'api/accountingInternalApi';
import { getDealsApi, getRemainingInstallmentsApi } from 'api/dealApi';
import { getFacilityFeesApi } from 'api/facilityFeeApi';
import { getMarketingMonthlySpendDataApi } from 'api/marketingApi';
import { getCustomerBusinessInfoApi } from 'api/underwritingApi';
import ContentBox from 'components/ContentBox';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import BackLink from 'components/links/BackLink';
import { TwDatePicker } from 'components/DatePicker';
import {
  getDefaultReceivingBankAccountId,
  getTwelveBankAccountLabel,
} from 'helpers/twelveBankAccounts';
import { useTwelveBankAccounts } from 'hooks/useTwelveBankAccounts';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isBillingDetailsComplete,
  mergeBillingDetails,
  normalizeBillingDetails,
} from './calc/billingDetails';
import {
  buildInvoiceNumber,
  formatRunningNumber,
  getNextRunningNumber,
} from './calc/invoiceNumber';
import { downloadInvoicePdf } from './calc/downloadInvoicePdf';
import { filterLiveCustomers } from './calc/liveCustomers';
import {
  createGrowthInvestmentLine,
  getPreviousMonthKey,
  nextGrowthInvestmentLine,
  persistedGrowthInvestments,
  sumGrowthInvestments,
} from './calc/growthInvestments';
import { resolveMarketingSpendForMonth } from './calc/marketingSpend';
import { marketingViewIdForFunding, pickLatestDeal } from './calc/pickLatestDeal';
import { isGrowthInvestmentComplete, suggestGrowthAmount } from './calc/suggestGrowthAmount';
import {
  computeDueDateFromLines,
  incompleteOtherChargeLines,
  suggestObligationLines,
  sumIncludedLineAmounts,
} from './calc/suggestObligations';
import { isSignaturePresent } from './calc/signatureStorage';
import BillingDetailsModal from './components/BillingDetailsModal';
import InvoicePreview from './components/InvoicePreview';
import InvoiceSignaturePanel from './components/InvoiceSignaturePanel';
import ObligationLinesTable from './components/ObligationLinesTable';
import SetOffFundingPanel from './components/SetOffFundingPanel';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUser();
  const invoiceDate = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  const [loadingLineIds, setLoadingLineIds] = useState({});
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [runningNumber, setRunningNumber] = useState('0001');
  const [obligationLines, setObligationLines] = useState([]);
  const [remainingInstallments, setRemainingInstallments] = useState([]);
  const [facilityFees, setFacilityFees] = useState([]);
  const [latestDeal, setLatestDeal] = useState(null);
  const [commitmentLength, setCommitmentLength] = useState(null);
  const [setOffEnabled, setSetOffEnabled] = useState(false);
  const [growthInvestments, setGrowthInvestments] = useState(() => [
    createGrowthInvestmentLine({ fundingMonthKey: getPreviousMonthKey() }),
  ]);
  const [growthInvestmentTouched, setGrowthInvestmentTouched] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [signature, setSignature] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [receivingBankAccountId, setReceivingBankAccountId] = useState('');
  const { bankAccounts, bankAccountOptions, isTwelveBankAccountsLoading } =
    useTwelveBankAccounts();

  useEffect(() => {
    Promise.all([getAllCustomersApi(), getCustomerPipelineApi()])
      .then(([items, pipelineReport]) => {
        setCustomers(
          filterLiveCustomers(items, pipelineReport)
            .map((customer) => ({
              label: `${customer.companyName} (${customer.externalId})`,
              value: customer.id,
              customer,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {
        toast({
          title: 'Error',
          description: 'Failed to load customers',
          status: 'error',
        });
      });
  }, [toast]);

  const invoiceNumber = useMemo(() => {
    if (!selectedCustomer?.externalId) {
      return '';
    }
    return buildInvoiceNumber({
      externalId: selectedCustomer.externalId,
      runningNumber,
    });
  }, [runningNumber, selectedCustomer]);

  const suggestedDueDate = useMemo(
    () => computeDueDateFromLines(obligationLines),
    [obligationLines]
  );

  useEffect(() => {
    if (!dueDateTouched) {
      setDueDate(suggestedDueDate);
    }
  }, [dueDateTouched, suggestedDueDate]);

  useEffect(() => {
    if (receivingBankAccountId) {
      return;
    }
    const defaultReceivingBankAccountId = getDefaultReceivingBankAccountId(bankAccounts);
    if (defaultReceivingBankAccountId) {
      setReceivingBankAccountId(defaultReceivingBankAccountId);
    }
  }, [bankAccounts, receivingBankAccountId]);

  const receivingBankLabel = useMemo(
    () => getTwelveBankAccountLabel(bankAccounts, receivingBankAccountId),
    [bankAccounts, receivingBankAccountId]
  );

  const onObligationLinesChange = (nextLines) => {
    setObligationLines(nextLines);
  };

  const loadMarketingSpend = async ({ customerId, monthKey, deal, lineId }) => {
    if (!customerId || !monthKey || !lineId) {
      return;
    }
    setLoadingLineIds((current) => ({ ...current, [lineId]: true }));
    try {
      const spendData = await getMarketingMonthlySpendDataApi({
        customerId,
        date: moment(`${monthKey}-01`).format('YYYY-MM-DD'),
        marketingViewId: marketingViewIdForFunding(deal),
      }).catch(() => []);
      const spend = resolveMarketingSpendForMonth(spendData, monthKey);
      const suggestion = suggestGrowthAmount({
        marketingSpend: spend,
        fundingPercentage: deal?.fundingPercentage,
      });
      setGrowthInvestments((current) =>
        current.map((line) =>
          line.id === lineId && line.fundingMonthKey === monthKey
            ? {
                ...line,
                marketingSpend: spend,
                growthInvestment: suggestion.suggestedAmount,
              }
            : line
        )
      );
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load marketing spend',
        status: 'error',
      });
    } finally {
      setLoadingLineIds((current) => {
        const next = { ...current };
        delete next[lineId];
        return next;
      });
    }
  };

  const loadCustomerData = async (customerId, customer) => {
    setLoadingCustomerData(true);
    let nextFundingMonth = null;
    let nextFundingLineId = null;
    let deal = null;
    try {
      const [businessInfo, installments, fees, deals, apiBilling] = await Promise.all([
        getCustomerBusinessInfoApi({ customerId }).catch(() => null),
        getRemainingInstallmentsApi({ customerId }),
        getFacilityFeesApi(customerId).catch(() => []),
        getDealsApi({ customerId }).catch(() => []),
        getCustomerBillingDetailsApi({ customerId }).catch(() => null),
      ]);

      const storedBilling =
        apiBilling?.invoiceCompanyName || apiBilling?.billingEmail ? apiBilling : null;
      const mergedBilling = mergeBillingDetails({
        businessInfo,
        companyName: customer.companyName,
        storedBilling,
        customerUsers: customer.users,
        allowedEmails: businessInfo?.allowedEmails,
      });
      setBillingDetails(mergedBilling);
      if (!isBillingDetailsComplete(mergedBilling)) {
        setBillingModalOpen(true);
      }

      deal = pickLatestDeal(deals);
      setLatestDeal(deal);
      setCommitmentLength(deal?.commitmentLength ?? null);

      const invoiceNumbers = await listCustomerInvoiceNumbersApi({ customerId }).catch(() => []);
      const nextRunning = getNextRunningNumber(invoiceNumbers);
      setRunningNumber(formatRunningNumber(nextRunning));

      setRemainingInstallments(installments || []);
      setFacilityFees(fees || []);
      const suggestedLines = suggestObligationLines({
        remainingInstallments: installments || [],
        facilityFees: fees || [],
        commitmentLength: deal?.commitmentLength ?? null,
      });
      setObligationLines(suggestedLines);
      setDueDateTouched(false);

      nextFundingMonth = getPreviousMonthKey();
      const firstLine = createGrowthInvestmentLine({ fundingMonthKey: nextFundingMonth });
      setGrowthInvestments([firstLine]);
      nextFundingLineId = firstLine.id;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customer invoice data',
        status: 'error',
      });
    } finally {
      setLoadingCustomerData(false);
    }

    if (nextFundingMonth && nextFundingLineId) {
      await loadMarketingSpend({
        customerId,
        monthKey: nextFundingMonth,
        deal,
        lineId: nextFundingLineId,
      });
    }
  };

  const onCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    const option = customers.find((item) => item.value === customerId);
    setSelectedCustomer(option?.customer ?? null);
    if (customerId && option?.customer) {
      loadCustomerData(customerId, option.customer);
    } else {
      setObligationLines([]);
      setBillingDetails(null);
      setDueDate(null);
      setDueDateTouched(false);
    }
  };

  const onGrowthInvestmentLineChange = (index, partial) => {
    const currentLine = growthInvestments[index];
    if (!currentLine) {
      return;
    }
    const nextLine = { ...currentLine, ...partial };
    setGrowthInvestments((current) =>
      current.map((line) => (line.id === currentLine.id ? nextLine : line))
    );
    if ('growthInvestment' in partial) {
      setGrowthInvestmentTouched(true);
    }
    if (
      partial.fundingMonthKey &&
      partial.fundingMonthKey !== currentLine.fundingMonthKey &&
      selectedCustomerId
    ) {
      loadMarketingSpend({
        customerId: selectedCustomerId,
        monthKey: partial.fundingMonthKey,
        deal: latestDeal,
        lineId: currentLine.id,
      });
    }
  };

  const onAddGrowthInvestment = () => {
    const nextLine = nextGrowthInvestmentLine(growthInvestments);
    setGrowthInvestments((current) => [...current, nextLine]);
    if (selectedCustomerId && nextLine.fundingMonthKey) {
      loadMarketingSpend({
        customerId: selectedCustomerId,
        monthKey: nextLine.fundingMonthKey,
        deal: latestDeal,
        lineId: nextLine.id,
      });
    }
  };

  const onRemoveGrowthInvestment = (index) => {
    setGrowthInvestments((current) => current.filter((_, lineIndex) => lineIndex !== index));
  };

  const onSaveBillingDetails = async (values) => {
    const nextBilling = normalizeBillingDetails(values, billingDetails);
    try {
      await upsertCustomerBillingDetailsApi({
        customerId: selectedCustomerId,
        billingDetails: nextBilling,
      });
      setBillingDetails(nextBilling);
      setBillingModalOpen(false);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save billing details',
        status: 'error',
      });
    }
  };

  const openRepaymentAmount = useMemo(
    () => sumIncludedLineAmounts(obligationLines),
    [obligationLines]
  );

  const onSetOffEnabledChange = (enabled) => {
    setSetOffEnabled(enabled);
    if (!enabled) {
      setGrowthInvestmentTouched(false);
      return;
    }
    const firstLine = growthInvestments[0];
    if (firstLine && !isGrowthInvestmentComplete(firstLine.growthInvestment)) {
      const suggestion = suggestGrowthAmount({
        marketingSpend: firstLine.marketingSpend,
        fundingPercentage: latestDeal?.fundingPercentage,
      });
      if (suggestion.suggestedAmount != null) {
        setGrowthInvestments((current) =>
          current.map((line) =>
            line.id === firstLine.id ? { ...line, growthInvestment: suggestion.suggestedAmount } : line
          )
        );
      }
    }
  };

  const validateSetOffFields = () => {
    if (!setOffEnabled) {
      return true;
    }
    if (growthInvestments.some((line) => !line.requestReceivedDate)) {
      toast({
        title: 'Missing request date',
        description: 'Enter the date the funding request was received.',
        status: 'warning',
      });
      return false;
    }
    if (growthInvestments.some((line) => !line.fundingMonthKey)) {
      toast({
        title: 'Missing funding month',
        description: 'Select the requested funding month.',
        status: 'warning',
      });
      return false;
    }
    if (growthInvestments.some((line) => !isGrowthInvestmentComplete(line.growthInvestment))) {
      setGrowthInvestmentTouched(true);
      toast({
        title: 'Growth investment required',
        description: 'Enter the growth investment amount for each funding month.',
        status: 'warning',
      });
      return false;
    }
    return true;
  };

  const buildCurrentInvoice = () => ({
    invoiceNumber,
    customerId: selectedCustomerId,
    externalId: selectedCustomer?.externalId,
    companyName: selectedCustomer?.companyName,
    invoiceDate,
    dueDate,
    totalDue: openRepaymentAmount,
    setOffEnabled,
    requestReceivedDate: setOffEnabled ? growthInvestments[0]?.requestReceivedDate : null,
    fundingMonthKey: setOffEnabled ? growthInvestments[0]?.fundingMonthKey : null,
    growthInvestment: setOffEnabled ? sumGrowthInvestments(growthInvestments) : null,
    growthInvestments: setOffEnabled ? persistedGrowthInvestments(growthInvestments) : undefined,
    setOffCreatedDate: setOffEnabled ? invoiceDate : null,
    billingDetails,
    lines: obligationLines,
    signature,
    receivingBankAccountId,
    receivingBankLabel,
  });

  // Every charge line needs the description and amount the ticket asks for;
  // the backend rejects an incomplete one, so catch it before the round trip.
  const validateChargeLines = () => {
    if (incompleteOtherChargeLines(obligationLines).length === 0) {
      return true;
    }
    toast({
      title: 'Incomplete charge',
      description: 'Every charge needs a description and an amount greater than zero.',
      status: 'warning',
    });
    return false;
  };

  const onDownloadPdf = async () => {
    if (!selectedCustomerId || !isBillingDetailsComplete(billingDetails)) {
      setBillingModalOpen(true);
      return;
    }
    if (!validateSetOffFields()) {
      return;
    }
    if (!validateChargeLines()) {
      return;
    }

    setDownloadingPdf(true);
    try {
      await downloadInvoicePdf(buildCurrentInvoice());
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        status: 'error',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const onSaveInvoice = async () => {
    if (!selectedCustomerId || !isBillingDetailsComplete(billingDetails)) {
      setBillingModalOpen(true);
      return;
    }
    if (!validateSetOffFields()) {
      return;
    }
    if (!validateChargeLines()) {
      return;
    }
    if (!isSignaturePresent(signature)) {
      toast({
        title: 'Signature required',
        description: 'Add your digital signature before saving the invoice.',
        status: 'warning',
      });
      return;
    }

    setSavingInvoice(true);
    try {
      const payload = {
        ...buildCurrentInvoice(),
        invoiceNumber,
        customerId: selectedCustomerId,
        externalId: selectedCustomer?.externalId,
        companyName: selectedCustomer?.companyName,
        createdByUserId: user?.userId || user?.email || null,
        createdByName: user?.name || user?.email || 'Unknown',
      };
      const saved = await createAccountingInvoiceApi(payload);
      toast({
        title: 'Invoice saved',
        description: `${invoiceNumber} saved successfully.`,
        status: 'success',
      });
      navigate(`/accounting-internal/${saved.id}`);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save invoice',
        status: 'error',
      });
    } finally {
      setSavingInvoice(false);
    }
  };

  return (
    <ContentBox>
      <BackLink text="Back to invoices" path="/accounting-internal" />
      <Flex mt={6} gap={6} align="flex-start" direction={{ base: 'column', xl: 'row' }}>
        <Box flex="1 1 60%" minW={0}>
          <Card mb={6} position="relative">
            <Heading variant="sectionHeader" borderColor="twelve.green.300" mb={6}>
              Create invoice
            </Heading>
            {loadingCustomerData && (
              <AbsoluteCenter bg="whiteAlpha.800" zIndex={1} borderRadius="inherit">
                <Spinner />
              </AbsoluteCenter>
            )}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={8}>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Customer
                </Text>
                <SelectInput
                  options={customers}
                  value={selectedCustomerId}
                  onChange={onCustomerChange}
                  enableSearch
                  placeholder="Select customer"
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Invoice number
                </Text>
                <Flex gap={2} align="center">
                  <Text fontWeight={700}>TRS-{selectedCustomer?.externalId ?? '—'}-</Text>
                  <Input
                    isDisabled={!selectedCustomer}
                    value={runningNumber}
                    maxW="120px"
                    onChange={(event) =>
                      setRunningNumber(formatRunningNumber(event.target.value))
                    }
                  />
                </Flex>
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Invoice date
                </Text>
                <Text fontWeight={700}>{moment(invoiceDate).format('MMMM DD, YYYY')}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Due date
                </Text>
                <TwDatePicker
                  value={dueDate}
                  onChange={(value) => {
                    setDueDateTouched(true);
                    setDueDate(value);
                  }}
                />
                <Text fontSize="xs" color="secondaryGray.600">
                  Defaults to earliest included obligation due date
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Receiving bank
                </Text>
                <SelectInput
                  options={bankAccountOptions}
                  value={receivingBankAccountId}
                  onChange={setReceivingBankAccountId}
                  placeholder="Select receiving bank"
                  isDisabled={isTwelveBankAccountsLoading}
                />
                <Text fontSize="xs" color="secondaryGray.600">
                  Defaults to Valley
                </Text>
              </Box>
            </SimpleGrid>

            {selectedCustomerId && (
              <>
                <Heading size="sm" mb={4}>
                  Payment obligations
                </Heading>
                <ObligationLinesTable
                  lines={obligationLines}
                  onChange={onObligationLinesChange}
                  remainingInstallments={remainingInstallments}
                  facilityFees={facilityFees}
                  commitmentLength={commitmentLength}
                />

                <Heading size="sm" mt={8} mb={4}>
                  Set-off against new funding
                </Heading>
                <SetOffFundingPanel
                  setOffEnabled={setOffEnabled}
                  onSetOffEnabledChange={onSetOffEnabledChange}
                  lines={growthInvestments}
                  onLineChange={onGrowthInvestmentLineChange}
                  onAddLine={onAddGrowthInvestment}
                  onRemoveLine={onRemoveGrowthInvestment}
                  loadingLineIds={loadingLineIds}
                  deal={latestDeal}
                  growthInvestmentInvalid={setOffEnabled && growthInvestmentTouched}
                  dueDate={dueDate}
                />

                <Box mt={8}>
                  <InvoiceSignaturePanel
                    userId={user?.userId || user?.email}
                    signerName={user?.name || user?.email || 'Unknown'}
                    signature={signature}
                    onSignatureChange={setSignature}
                  />
                </Box>

                <Flex justify="flex-end" gap={3} mt={8}>
                  <Button variant="outline" onClick={() => navigate('/accounting-internal')}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    isLoading={downloadingPdf}
                    isDisabled={!selectedCustomerId}
                    onClick={onDownloadPdf}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="brand"
                    onClick={onSaveInvoice}
                    isLoading={savingInvoice}
                    isDisabled={!isSignaturePresent(signature)}
                  >
                    Save invoice
                  </Button>
                </Flex>
              </>
            )}
          </Card>
        </Box>

        <Box flex="1 1 40%" minW={{ base: '100%', xl: '420px' }} position="sticky" top="24px">
          {selectedCustomerId && billingDetails && (
            <InvoicePreview
              invoiceNumber={invoiceNumber}
              invoiceDate={invoiceDate}
              dueDate={dueDate}
              billingDetails={billingDetails}
              lines={obligationLines}
              setOffEnabled={setOffEnabled}
              requestReceivedDate={growthInvestments[0]?.requestReceivedDate}
              fundingMonthKey={growthInvestments[0]?.fundingMonthKey}
              growthInvestment={sumGrowthInvestments(growthInvestments)}
              growthInvestments={growthInvestments}
              setOffCreatedDate={setOffEnabled ? invoiceDate : null}
              signature={signature}
            />
          )}
        </Box>
      </Flex>

      <BillingDetailsModal
        isOpen={billingModalOpen}
        billingDetails={billingDetails}
        onClose={() => setBillingModalOpen(false)}
        onSave={onSaveBillingDetails}
      />
    </ContentBox>
  );
};

export default CreateInvoice;
